import { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import type { AuthUser as User, AuthSession as Session } from '@/lib/auth';
import { auth } from "@/lib/auth";
import { db } from "@/lib/db";
import { realtime } from "@/lib/realtime";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { migrateOldPreferences } from "@/utils/migratePreferences";
import { toast } from "sonner";
import { fetchApi } from "@/lib/api/fetch-api";

export interface TenantSummary {
  id: string;
  slug: string;
  nome: string;
  tipo: 'organization' | 'loja';
}

export interface UserRole {
  role: "admin" | "gerente" | "producao" | "super_admin" | "org_admin";
  setor: string | null;          // Retrocompat: espelho de setor_ativo
  setores: string[];             // Array de setores autorizados
  modulos: string[];             // Array de módulos autorizados
  setor_ativo: string | null;    // Setor selecionado atualmente
  setor_padrao: string | null;   // Setor default
  nome_completo: string;
  // Multi-tenant (Story SAAS-2.2 + SAAS-7.1)
  tenantId?: string | null;
  activeTenantId?: string | null;
  availableTenants?: TenantSummary[];
  organizationId?: string | null; // Organization do user (parent das lojas)
  isSuperAdmin?: boolean;
  isOrgAdmin?: boolean;
  /** ID do vendedor no Gestão Click (mesclagem pelo vendedor responsável) */
  gc_vendedor_id?: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  userRole: UserRole | null;
  loading: boolean;
  roleLoading: boolean;
  isRedirecting: boolean;
  signOut: () => Promise<void>;
  setActiveSector: (sector: string) => Promise<void>;
  /**
   * Re-busca availableTenants/activeTenantId do backend e atualiza o estado +
   * localStorage SEM reload. Usado após importar lojas / salvar integração GC,
   * para o sidebar refletir novas lojas sem `window.location.reload()`.
   */
  refreshContext: () => Promise<TenantSummary[] | null>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  userRole: null,
  loading: true,
  roleLoading: true,
  isRedirecting: false,
  signOut: async () => {},
  setActiveSector: async () => {},
  refreshContext: async () => null,
});

export const useAuth = () => useContext(AuthContext);

// Rotas válidas por role (fora do componente para evitar recriação)
// IMPORTANTE: toda nova rota em App.tsx precisa entrar aqui (manager/operator) ou
// em authRoutes — caso contrário shouldRedirect dispara navigate('/') no F5.
const managerRoutes = [
  '/', '/scheduling', '/intelligence', '/retrabalho', '/relatorios', '/logistics', '/logistics/history',
  '/arte', '/comercial', '/painel',
  '/integracoes', '/como-funciona', '/usuarios', '/admin', '/admin/automacao',
  '/feedbacks', '/cadastros', '/feriados',
  // Estoque (0080/0081): /estoquista entra aqui também porque a decisão foi dar
  // a tela do posto ao gerente — sem isso um F5 nela o jogaria para o dashboard.
  '/estoque', '/estoquista', '/compras-demandas',
];
const operatorRoutes = ['/operador', '/arte', '/comercial', '/logistics', '/logistics/history', '/estoquista', '/compras-demandas'];
const authRoutes = ['/auth', '/auth/reset-password'];

// Mapeamento completo de setores para suas rotas
const SECTOR_ROUTES: Record<string, string> = {
  // Rotas dedicadas (módulos especiais)
  'ARTE': '/arte',
  'COMERCIAL': '/comercial',
  'LOGISTICA': '/logistics',
  // Setores de produção (vão para /operador)
  'CORTE': '/operador',
  'COSTURA': '/operador',
  'SILK': '/operador',
  'DTF': '/operador',
  'SUBLIMACAO': '/operador',
  'BORDADO': '/operador',
  'IMPRESSAO': '/operador',
  'SAIDA_ESTOQUE': '/operador',
  'COMPRAS': '/operador',
  'EXPEDICAO': '/operador',
  // Posto de bipagem (0081). Não é setor de produção: não tem card nem quadro,
  // por isso rota própria em vez de /operador.
  'ESTOQUE': '/estoquista',
};

// Função para determinar a rota correta baseado no role e setor
const getRedirectPath = (role: string, setor: string | null): string => {
  // super_admin vai pro dashboard de admin (mesma home dos admins normais)
  if (role === 'super_admin' || role === 'admin' || role === 'gerente') return '/';

  if (setor && SECTOR_ROUTES[setor]) {
    return SECTOR_ROUTES[setor];
  }

  return '/operador';
};

// Função para normalizar paths com query params malformados
const normalizePathname = (pathname: string): { path: string; hasEncodedParams: boolean } => {
  if (pathname.includes('%3F')) {
    const decodedPath = decodeURIComponent(pathname);
    if (decodedPath.startsWith('/?')) {
      return { path: '/', hasEncodedParams: true };
    }
  }
  return { path: pathname, hasEncodedParams: false };
};

const shouldRedirect = (currentPath: string, role: string, setor: string | null) => {
  const { path: normalizedPath } = normalizePathname(currentPath);

  const isManager = role === 'super_admin' || role === 'admin' || role === 'gerente';
  const expectedRoute = getRedirectPath(role, setor);
  
  if (normalizedPath === expectedRoute) return false;
  if (authRoutes.includes(normalizedPath)) return true;
  
  if (isManager) {
    return !managerRoutes.includes(normalizedPath) && !normalizedPath.startsWith('/logistics');
  } else {
    return !operatorRoutes.includes(normalizedPath);
  }
};

// Função para converter dados do banco para UserRole
// IMPORTANTE: super_admin e org_admin são mapeados como 'admin' no role
// efetivo para preservar compatibilidade com todos os checks `role === 'admin'`
// existentes no código. A diferenciação fica via flags isSuperAdmin/isOrgAdmin.
const parseUserRole = (data: any): UserRole => {
  const isSuper = data.isSuperAdmin ?? (data.role === 'super_admin');
  const isOrg = data.isOrgAdmin ?? (data.role === 'org_admin');
  const effectiveRole = (isSuper || isOrg) ? 'admin' : data.role;

  return {
    role: effectiveRole,
    setor: data.setor_ativo ?? data.setor ?? null,
    setores: data.setores ?? (data.setor ? [data.setor] : []),
    modulos: data.modulos ?? ['operador'],
    setor_ativo: data.setor_ativo ?? data.setor ?? null,
    setor_padrao: data.setor_padrao ?? data.setor ?? null,
    nome_completo: data.nome_completo,
    // Multi-tenant (Story SAAS-2.2 + SAAS-7.1)
    tenantId: data.tenantId ?? data.tenant_id ?? null,
    activeTenantId: data.activeTenantId ?? data.active_tenant_id ?? data.tenantId ?? null,
    availableTenants: data.availableTenants ?? data.available_tenants ?? [],
    organizationId: data.organizationId ?? data.organization_id ?? null,
    isSuperAdmin: isSuper,
    isOrgAdmin: isOrg,
    gc_vendedor_id: data.gcVendedorId ?? data.gc_vendedor_id ?? null,
  };
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [roleLoading, setRoleLoading] = useState(true);
  const [isRedirecting, setIsRedirecting] = useState(false);
  
  const hasNavigatedRef = useRef(false);
  const isInitialMount = useRef(true);
  const isInitializingRef = useRef(true);
  const initTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Função centralizada para buscar role
  // Express adapter: JWT já contém claims de role — usa direto sem query extra
  // Supabase adapter: faz query ao banco
  const fetchUserRole = useCallback(async (userId: string, retryCount = 0): Promise<UserRole | null> => {
    const MAX_RETRIES = 3;

    try {
      setRoleLoading(true);

      // Tentar extrair role dos dados do JWT (Express adapter armazena em localStorage)
      const authUserStr = localStorage.getItem('auth_user');
      if (authUserStr) {
        try {
          const authUser = JSON.parse(authUserStr);
          if (authUser.role && (authUser.userId === userId || authUser.id === userId)) {
            const parsedRole = parseUserRole({
              role: authUser.role,
              setor: authUser.setor || authUser.setorAtivo,
              setores: authUser.setores || [],
              modulos: authUser.modulos || [],
              setor_ativo: authUser.setorAtivo || authUser.setor_ativo || authUser.setor,
              setor_padrao: authUser.setor_padrao || authUser.setorPadrao || authUser.setor,
              nome_completo: authUser.nomeCompleto || authUser.nome_completo || authUser.email || 'User',
              // Multi-tenant fields do JWT (Story SAAS-2.2 + 7.1)
              tenantId: authUser.tenantId,
              activeTenantId: authUser.activeTenantId,
              availableTenants: authUser.availableTenants,
              organizationId: authUser.organizationId,
              isSuperAdmin: authUser.isSuperAdmin,
              isOrgAdmin: authUser.isOrgAdmin,
              gcVendedorId: authUser.gcVendedorId ?? authUser.gc_vendedor_id ?? null,
            });
            setUserRole(parsedRole);
            setRoleLoading(false);
            if (parsedRole) migrateOldPreferences(userId);
            return parsedRole;
          }
        } catch { /* fall through to db query */ }
      }

      // Fallback: query database (Supabase adapter)
      const { data: roleData, error } = await db
        .from("user_roles")
        .select("role, setor, setores, modulos, setor_ativo, setor_padrao, nome_completo")
        .eq("user_id", userId)
        .single();

      if (error) {
        console.error('[AuthContext] Error fetching user role:', error);

        if (retryCount < MAX_RETRIES) {
          console.log(`[AuthContext] Retrying fetchUserRole (${retryCount + 1}/${MAX_RETRIES})...`);
          await new Promise(resolve => setTimeout(resolve, 1000));
          return fetchUserRole(userId, retryCount + 1);
        }

        console.error('[AuthContext] Max retries exceeded, userRole will be null');
        setUserRole(null);
        setRoleLoading(false);
        return null;
      }

      const parsedRole = roleData ? parseUserRole(roleData) : null;
      setUserRole(parsedRole);
      setRoleLoading(false);

      if (parsedRole) {
        migrateOldPreferences(userId);
      }

      return parsedRole;
    } catch (err) {
      console.error('[AuthContext] Unexpected error fetching role:', err);
      setRoleLoading(false);
      return null;
    }
  }, []);

  // Função para trocar o setor ativo (navegação tratada por useEffect separado)
  const setActiveSector = useCallback(async (sector: string) => {
    if (!user) return;
    
    try {
      const { error } = await db.rpc('set_active_sector', { _sector: sector });
      
      if (error) {
        console.error('[AuthContext] Error setting active sector:', error);
        throw error;
      }
      
      // Invalidar todas as queries que dependem do setor
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['production-steps'] }),
        queryClient.invalidateQueries({ queryKey: ['all-production-steps'] }),
        queryClient.invalidateQueries({ queryKey: ['standalone-purchases'] }),
        queryClient.invalidateQueries({ queryKey: ['logistics-orders'] }),
      ]);
      
      // Atualizar estado local - navegação será tratada pelo useEffect reativo
      setUserRole(prev => prev ? { 
        ...prev, 
        setor_ativo: sector, 
        setor: sector 
      } : null);
      
      console.log('[AuthContext] Setor ativo alterado para:', sector);
      
    } catch (error) {
      console.error('[AuthContext] Failed to set active sector:', error);
      throw error;
    }
  }, [user, queryClient]);

  // Re-busca o contexto multi-tenant do backend (novo token + availableTenants)
  // e atualiza estado + localStorage SEM reload. Retorna a nova lista de lojas.
  const refreshContext = useCallback(async (): Promise<TenantSummary[] | null> => {
    try {
      const resp = await fetchApi<any>('/api/auth/refresh-context', { method: 'POST' });
      const data = resp?.data ?? resp;
      const newToken: string | undefined = data?.access_token ?? data?.token;
      const newAvailable: TenantSummary[] =
        data?.availableTenants ?? data?.available_tenants ?? [];
      const newActive: string | null | undefined =
        data?.activeTenantId ?? data?.active_tenant_id;

      if (newToken) localStorage.setItem('auth_token', newToken);

      // Mescla no auth_user para sobreviver a um reload futuro
      try {
        const authUserStr = localStorage.getItem('auth_user');
        const authUser = authUserStr ? JSON.parse(authUserStr) : {};
        authUser.availableTenants = newAvailable;
        if (newActive !== undefined) authUser.activeTenantId = newActive;
        localStorage.setItem('auth_user', JSON.stringify(authUser));
      } catch {
        /* localStorage indisponível — segue só com o estado em memória */
      }

      // Atualiza o estado → sidebar re-renderiza sem reload
      setUserRole(prev =>
        prev
          ? {
              ...prev,
              availableTenants: newAvailable,
              activeTenantId: newActive ?? prev.activeTenantId,
            }
          : prev,
      );

      return newAvailable;
    } catch (err) {
      console.error('[AuthContext] refreshContext failed:', err);
      return null;
    }
  }, []);

  // Corrigir URLs malformadas
  useEffect(() => {
    if (location.pathname.includes('%3F')) {
      const decodedPath = decodeURIComponent(location.pathname);
      if (decodedPath.startsWith('/?')) {
        const searchPart = decodedPath.substring(2);
        console.log('🔧 Corrigindo URL malformada:', location.pathname, '→', '/?' + searchPart);
        navigate('/' + '?' + searchPart, { replace: true });
      }
    }
  }, [location.pathname, navigate]);

  // Listener para mudanças de setor_ativo em outras abas
  useEffect(() => {
    if (!user?.id) return;

    // Walter 2026-05-18 — revisão realtime #0 (parte 2): removido guard
    // `isSystemActive()`. Mesmo motivo dos hooks de realtime (ver
    // useProductionCoreSync). Sem isso, mudança de setor em outra aba
    // não refletia até refresh quando fora 5-20h BRT ou domingo.

    const channel = realtime
      .channel('user-role-sync')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'user_roles',
        filter: `user_id=eq.${user.id}`
      }, (payload) => {
        const newData = payload.new as any;

        // Walter 2026-05-19 — Bug 31: se admin desativa o user em outra aba,
        // revoga a sessão imediatamente em vez de esperar próximo login.
        // Backend já bloqueia login com ativo=false (auth.ts:197), mas sessão
        // ativa precisava de refresh manual antes deste fix.
        if (newData.ativo === false) {
          console.warn('[AuthContext] Usuário desativado em outra sessão — forçando logout');
          toast.error('Sua conta foi desativada. Faça login novamente.');
          auth.signOut().then(() => {
            setUser(null);
            setSession(null);
            setUserRole(null);
            hasNavigatedRef.current = false;
            navigate('/auth');
          });
          return;
        }

        console.log('[AuthContext] Realtime update received:', newData.setor_ativo);

        setUserRole(prev => prev ? {
          ...prev,
          setor_ativo: newData.setor_ativo,
          setor: newData.setor_ativo,
          setores: newData.setores ?? prev.setores,
          modulos: newData.modulos ?? prev.modulos,
        } : null);

        // Invalidar queries quando setor muda em outra aba
        queryClient.invalidateQueries({ queryKey: ['production-steps'] });
        queryClient.invalidateQueries({ queryKey: ['all-production-steps'] });
      })
      .subscribe();
      
    return () => {
      realtime.removeChannel(channel);
    };
  }, [user?.id, queryClient]);

  // useEffect principal - único caminho de inicialização
  useEffect(() => {
    const { data: { subscription } } = auth.onAuthStateChange(
      (event, session) => {
        console.log('🔐 Auth State Change:', event, 'isInitializing:', isInitializingRef.current, 'hasNavigated:', hasNavigatedRef.current);
        
        // IGNORAR eventos durante inicialização - deixar initializeAuth tratar
        if (isInitializingRef.current) {
          console.log('🔐 Ignorando evento durante inicialização');
          return;
        }
        
        // ═══════════════════════════════════════════════════════════════
        // FIX: Tratar erro de refresh token inválido
        // Quando TOKEN_REFRESHED acontece sem session, forçar logout limpo
        // ═══════════════════════════════════════════════════════════════
        if (event === 'TOKEN_REFRESHED' && !session) {
          console.log('🔐 Token refresh falhou - forçando logout limpo');
          auth.signOut({ scope: 'local' }).then(() => {
            setUser(null);
            setSession(null);
            setUserRole(null);
            setLoading(false);
            setRoleLoading(false);
            hasNavigatedRef.current = false;
            navigate('/auth');
          });
          return;
        }
        
        // Apenas atualizar sessão/usuário
        setSession(session);
        setUser(session?.user ?? null);
        
        // Se deslogou, limpar tudo
        if (!session?.user) {
          setUserRole(null);
          setRoleLoading(false);
          hasNavigatedRef.current = false;
          return;
        }
        
        // Para eventos de LOGIN ou TOKEN_REFRESHED, buscar role e navegar
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          setTimeout(async () => {
            const parsedRole = await fetchUserRole(session.user.id);
            
            if (parsedRole && !hasNavigatedRef.current) {
              const currentPath = window.location.pathname;
              if (shouldRedirect(currentPath, parsedRole.role, parsedRole.setor)) {
                const redirectTo = getRedirectPath(parsedRole.role, parsedRole.setor);
                console.log('🚀 Navegação pós-evento:', redirectTo);
                setIsRedirecting(true);
                navigate(redirectTo);
                setTimeout(() => setIsRedirecting(false), 100);
              }
              hasNavigatedRef.current = true;
            }
          }, 0);
        }
      }
    );

    // Inicialização única via getSession
    const initializeAuth = async () => {
      // Safety timeout: força saída do loading após 15s
      initTimeoutRef.current = setTimeout(() => {
        console.error('[AuthContext] initializeAuth timeout (15s) - forçando saída do loading');
        setLoading(false);
        setRoleLoading(false);
      }, 15000);

      try {
        const { data: { session } } = await auth.getSession();
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          const parsedRole = await fetchUserRole(session.user.id);
          
          if (parsedRole && !hasNavigatedRef.current && isInitialMount.current) {
            const currentPath = window.location.pathname;
            
            if (shouldRedirect(currentPath, parsedRole.role, parsedRole.setor)) {
              const redirectTo = getRedirectPath(parsedRole.role, parsedRole.setor);
              console.log('🚀 Navegação inicial (getSession):', redirectTo);
              setIsRedirecting(true);
              navigate(redirectTo);
              setTimeout(() => setIsRedirecting(false), 100);
            }
            
            hasNavigatedRef.current = true;
          }
        } else {
          setRoleLoading(false);
        }
      } finally {
        if (initTimeoutRef.current) clearTimeout(initTimeoutRef.current);
        setLoading(false);
        isInitialMount.current = false;
        isInitializingRef.current = false;
      }
    };
    
    initializeAuth();

    return () => subscription.unsubscribe();
  }, [navigate, fetchUserRole]);

  const signOut = async () => {
    await auth.signOut();
    setUser(null);
    setSession(null);
    setUserRole(null);
    hasNavigatedRef.current = false;
    navigate("/auth");
  };

  return (
    <AuthContext.Provider value={{ user, session, userRole, loading, roleLoading, isRedirecting, signOut, setActiveSector, refreshContext }}>
      {children}
    </AuthContext.Provider>
  );
}
